import { PlayerNamingConfig } from '@/types/core'
import { ref, computed } from 'vue'
import { pseudoData } from '@/core/player/pseudoData'
import sampleConfigJson from '@/../public/parties/party-2.json'

export type PartyStep = 'configuration' | 'party-active' | 'game-running'

export interface PartyData {
  id?: string
  playerLimit: number
  globalSettings: {
    allowSpectators: boolean
    maxReconnectionTime: number
    assetCacheSize: number
    performanceMode: boolean,
    playerNaming: PlayerNamingConfig,
  }
  games: Array<{
    gameplaySlug: string
    name: string
    config: any
    assets: Array<{
      id: string
      type: string
      url: string
      name: string
      checksum: string
      size: number
      mimeType: string
      metadata: any
    }>
    timerDuration: number
    scoreVisibility: string
  }>
}

export function usePartyState() {
  // Current step in the party setup flow
  const currentStep = ref<PartyStep>('configuration')

  // Party configuration data
  const partyData = ref<PartyData | null>(null)

  // Validation results
  const validationResults = ref<{
    totalAssets: number
    accessibleAssets: number
  } | null>(null)

  // Sample configuration for initialization
  const sampleConfig: PartyData = sampleConfigJson as PartyData

  // Initialize with sample config
  function initializeSampleConfig() {
    partyData.value = { ...sampleConfig }
  }

  // Validate configuration
  function validateConfiguration(config: string): { success: boolean; error?: string } {
    try {
      const parsed = JSON.parse(config)

      // Basic structure validation
      if (!parsed.playerLimit || parsed.playerLimit < 1 || parsed.playerLimit > 40) {
        throw new Error('playerLimit must be between 1 and 40')
      }

      if (!parsed.games || !Array.isArray(parsed.games)) {
        throw new Error('games must be an array')
      }

      // Validate pseudo names availability
      const playerNaming = parsed.globalSettings?.playerNaming
      console.log('🔍 Validating pseudo names:', {
        playerLimit: parsed.playerLimit,
        playerNaming,
        availableNames: pseudoData.names.length
      })

      if (playerNaming && playerNaming.randomNamesCount > 0) {
        const { playerLimit } = parsed
        const { randomNamesCount } = playerNaming
        const availableNames = pseudoData.names.length

        console.log('📊 Pseudo validation check:', {
          playerLimit,
          randomNamesCount,
          availableNames,
          allowCustomPseudo: playerNaming.allowCustomPseudo
        })

        // Always validate if we're generating random names, regardless of custom pseudo setting
        // Because even with custom pseudo allowed, players might still request random options
        const requiredNames = playerLimit * randomNamesCount

        if (requiredNames > availableNames) {
          const errorMsg = `Insufficient pseudo names available. Requested: ${playerLimit} players × ${randomNamesCount} options = ${requiredNames} names, ` +
            `but only ${availableNames} unique names available in database. ` +
            `Try reducing playerLimit (${playerLimit}) or randomNamesCount (${randomNamesCount}).`
          console.error('❌ Pseudo validation failed:', errorMsg)
          throw new Error(errorMsg)
        }

        // Additional warning for tight constraints
        if (requiredNames > availableNames * 0.8) {
          console.warn(
            `⚠️ Pseudo names capacity warning: Using ${requiredNames}/${availableNames} available names (${Math.round(requiredNames/availableNames*100)}%). ` +
            `Consider reducing playerLimit or randomNamesCount for better variety.`
          )
        }

        console.log('✅ Pseudo validation passed:', `${requiredNames}/${availableNames} names`)
      }

      // Success - update party data
      partyData.value = parsed
      validationResults.value = {
        totalAssets: parsed.games.reduce((sum: number, game: any) => sum + (game.assets?.length || 0), 0),
        accessibleAssets: parsed.games.reduce((sum: number, game: any) => sum + (game.assets?.length || 0), 0)
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Invalid JSON'
      }
    }
  }

  // Generate unique party ID
  function generatePartyId(): string {
    return 'party-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9)
  }

  // Navigation methods
  function goToStep(step: PartyStep) {
    currentStep.value = step
  }

  function nextStep() {
    const steps: PartyStep[] = ['configuration', 'party-active', 'game-running']
    const currentIndex = steps.indexOf(currentStep.value)
    if (currentIndex < steps.length - 1 && currentIndex !== -1) {
      currentStep.value = steps[currentIndex + 1]!
    }
  }

  function previousStep() {
    const steps: PartyStep[] = ['configuration', 'party-active', 'game-running']
    const currentIndex = steps.indexOf(currentStep.value)
    if (currentIndex > 0) {
      currentStep.value = steps[currentIndex - 1]!
    }
  }

  // Computed values
  const joinUrl = computed(() => {
    if (!partyData.value?.id) return ''
    return `${window.location.origin}/player-vue.html?party=${partyData.value.id}`
  })

  const tvJoinUrl = computed(() => {
    if (!partyData.value?.id) return ''
    return `${window.location.origin}/tv-vue.html?party=${partyData.value.id}`
  })

  const isConfigurationValid = computed(() => {
    return partyData.value !== null
  })

  const stepIndex = computed(() => {
    const steps: PartyStep[] = ['configuration', 'party-active', 'game-running']
    return steps.indexOf(currentStep.value)
  })

  return {
    // State
    currentStep,
    partyData,
    validationResults,
    sampleConfig,

    // Methods
    initializeSampleConfig,
    validateConfiguration,
    generatePartyId,
    goToStep,
    nextStep,
    previousStep,

    // Computed
    joinUrl,
    tvJoinUrl,
    isConfigurationValid,
    stepIndex
  }
}
